import * as ImagePicker from 'expo-image-picker';
import * as ImageManipulator from 'expo-image-manipulator';
import { File as ExpoFile } from 'expo-file-system';

export interface PickedImage {
  uri: string;
  width: number;
  height: number;
}

export interface CompressedImage {
  uri: string;
  width: number;
  height: number;
  thumbnailDataUrl: string;
}

const MAX_LONG_EDGE = 1568;
const JPEG_QUALITY = 0.7;
const THUMB_LONG_EDGE = 80;

export class ChatImageManager {
  static async requestCameraPermission(): Promise<boolean> {
    const { status } = await ImagePicker.requestCameraPermissionsAsync();
    return status === 'granted';
  }

  static async requestLibraryPermission(): Promise<boolean> {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    return status === 'granted';
  }

  static async pickFromCamera(): Promise<PickedImage | null> {
    const result = await ImagePicker.launchCameraAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 1,
      exif: false,
    });
    if (result.canceled || !result.assets?.[0]) return null;
    const asset = result.assets[0];
    return { uri: asset.uri, width: asset.width, height: asset.height };
  }

  static async pickFromLibrary(): Promise<PickedImage | null> {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 1,
      exif: false,
    });
    if (result.canceled || !result.assets?.[0]) return null;
    const asset = result.assets[0];
    return { uri: asset.uri, width: asset.width, height: asset.height };
  }

  static async compressForUpload(picked: PickedImage): Promise<CompressedImage> {
    const longEdge = Math.max(picked.width, picked.height);
    const scale = longEdge > MAX_LONG_EDGE ? MAX_LONG_EDGE / longEdge : 1;
    const targetWidth = Math.round(picked.width * scale);

    const compressed = await ImageManipulator.manipulateAsync(
      picked.uri,
      scale < 1 ? [{ resize: { width: targetWidth } }] : [],
      { compress: JPEG_QUALITY, format: ImageManipulator.SaveFormat.JPEG },
    );

    const thumbScale = THUMB_LONG_EDGE / Math.max(compressed.width, compressed.height);
    const thumb = await ImageManipulator.manipulateAsync(
      compressed.uri,
      [{ resize: { width: Math.round(compressed.width * thumbScale) } }],
      { compress: 0.6, format: ImageManipulator.SaveFormat.JPEG, base64: true },
    );

    return {
      uri: compressed.uri,
      width: compressed.width,
      height: compressed.height,
      thumbnailDataUrl: `data:image/jpeg;base64,${thumb.base64 ?? ''}`,
    };
  }

  static async toDataUrl(uri: string): Promise<string> {
    const file = new ExpoFile(uri);
    const base64 = file.base64Sync();
    return `data:image/jpeg;base64,${base64}`;
  }

  static async purge(uri: string | null | undefined): Promise<void> {
    if (!uri || !uri.startsWith('file://')) return;
    try {
      const file = new ExpoFile(uri);
      if (file.exists) {
        file.delete();
      }
    } catch (err) {
      console.warn('[ChatImageManager:purge]', err);
    }
  }
}
